import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  CheckCircle2, XCircle, Clock, Loader2, AlertTriangle,
  ArrowLeft, BarChart3, Video, Waves, Timer, Ban,
  ChevronRight, RefreshCw, Eye
} from 'lucide-react';
import { checkSessionStatus } from '../services/biomechanicsService';
import { AZURE } from '../services/api';

// ── Design tokens ────────────────────────────────────────────────
const V = {
  bg: '#0f1117',
  card: '#1a1d27',
  cardBorder: '#2a2d37',
  accent: '#06b6d4',     // cyan-500
  accentDark: '#0891b2',
  success: '#22c55e',
  error: '#ef4444',
  warning: '#f59e0b',
  text: '#f1f5f9',
  textMuted: '#94a3b8',
  textDim: '#64748b',
};

// ── Stage definitions ────────────────────────────────────────────
const STAGES = [
  { key: 'uploaded',    label: 'Upload concluído',         icon: Video },
  { key: 'queued',      label: 'Na fila de processamento', icon: Clock },
  { key: 'preparing',   label: 'Preparando análise',       icon: RefreshCw },
  { key: 'analyzing',   label: 'Analisando vídeo',         icon: Eye },
  { key: 'completed',   label: 'Finalizado',               icon: CheckCircle2 },
];

function mapStatusToStageIndex(status) {
  switch (status) {
    case 'pending':
    case 'queued':
    case 'scanning':
      return 1; // queued
    case 'processing':
      return 3; // analyzing
    case 'completed':
      return 4;
    case 'failed':
    case 'cancelled':
      return -1; // terminal
    default:
      return 0;
  }
}

function getStageMessage(status) {
  switch (status) {
    case 'pending':   return 'Sua sessão está na fila. O worker vai processá-la em breve.';
    case 'queued':    return 'Aguardando vez na fila de processamento...';
    case 'scanning':  return 'Escaneando raias da piscina...';
    case 'processing': return 'Análise biomecânica em andamento. Detectando movimentos, contando braçadas e calculando métricas...';
    case 'completed': return 'Análise concluída com sucesso! Seus resultados estão prontos.';
    case 'failed':    return 'Ocorreu um erro durante o processamento.';
    case 'cancelled': return 'Processamento cancelado.';
    default:          return 'Verificando status...';
  }
}

function formatElapsed(seconds) {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

// ── Main Component ───────────────────────────────────────────────
export default function SessionStatusPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [cancelling, setCancelling] = useState(false);
  const startRef = useRef(Date.now());
  const pollRef = useRef(null);
  const timerRef = useRef(null);

  // ── Poll session status ──
  const fetchStatus = useCallback(async () => {
    try {
      const data = await checkSessionStatus(sessionId);
      setSession(data);
      setError(null);

      // Auto-redirect on completion
      if (data?.status === 'completed') {
        clearInterval(pollRef.current);
        clearInterval(timerRef.current);
        setTimeout(() => {
          navigate(`/swim-vision/result/${sessionId}`, { replace: true });
        }, 2500);
      }

      // Stop polling on terminal states
      if (['failed', 'cancelled'].includes(data?.status)) {
        clearInterval(pollRef.current);
        clearInterval(timerRef.current);
      }
    } catch (err) {
      setError(err.message || 'Erro ao verificar status');
    }
  }, [sessionId, navigate]);

  useEffect(() => {
    fetchStatus();
    pollRef.current = setInterval(fetchStatus, 3000);
    timerRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
    }, 1000);

    return () => {
      clearInterval(pollRef.current);
      clearInterval(timerRef.current);
    };
  }, [fetchStatus]);

  // ── Cancel handler ──
  const handleCancel = async () => {
    if (cancelling) return;
    setCancelling(true);
    try {
      await AZURE.post(`/vukz/biomechanics/sessions/${sessionId}/cancel`);
      await fetchStatus();
    } catch (err) {
      setError('Falha ao cancelar: ' + (err.message || ''));
    } finally {
      setCancelling(false);
    }
  };

  const status = session?.status || 'pending';
  const currentStageIdx = mapStatusToStageIndex(status);
  const isTerminal = ['completed', 'failed', 'cancelled'].includes(status);
  const canCancel = !isTerminal && !cancelling;
  const progressPct = status === 'completed' ? 100
    : status === 'processing' ? 65
    : status === 'scanning' ? 40
    : status === 'pending' ? 15
    : 0;

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: V.bg }}>
      <div className="w-full max-w-lg">

        {/* ── Header ── */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold mb-4"
               style={{ backgroundColor: V.accent + '20', color: V.accent }}>
            <Waves size={14} /> SWIM VISION AI
          </div>
          <h1 className="text-2xl font-black" style={{ color: V.text }}>
            Sessão #{sessionId}
          </h1>
          <p className="text-sm mt-1" style={{ color: V.textMuted }}>
            {session?.swimming_style || 'freestyle'} · {session?.video_filename || ''}
          </p>
        </div>

        {/* ── Main Card ── */}
        <div className="rounded-2xl border p-6" style={{ backgroundColor: V.card, borderColor: V.cardBorder }}>

          {/* Progress bar */}
          <div className="mb-6">
            <div className="flex justify-between text-xs font-bold mb-2" style={{ color: V.textMuted }}>
              <span>Progresso</span>
              <span>{progressPct}%</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: V.cardBorder }}>
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: `${progressPct}%`,
                  backgroundColor: status === 'failed' ? V.error
                    : status === 'cancelled' ? V.textDim
                    : status === 'completed' ? V.success
                    : V.accent,
                }}
              />
            </div>
          </div>

          {/* Stage steps */}
          <div className="space-y-3 mb-6">
            {STAGES.map((stage, i) => {
              const Icon = stage.icon;
              let state = 'pending'; // pending | active | done | error
              if (status === 'failed' && i <= Math.max(currentStageIdx, 2)) {
                state = i < Math.max(currentStageIdx, 2) ? 'done' : 'error';
              } else if (status === 'cancelled') {
                state = i <= Math.max(currentStageIdx, 1) ? 'done' : 'pending';
                if (i === Math.max(currentStageIdx, 1) + 1) state = 'cancelled';
              } else if (i < currentStageIdx) {
                state = 'done';
              } else if (i === currentStageIdx) {
                state = 'active';
              }

              const colors = {
                done: { bg: V.success + '20', icon: V.success, text: V.text },
                active: { bg: V.accent + '20', icon: V.accent, text: V.text },
                error: { bg: V.error + '20', icon: V.error, text: V.error },
                cancelled: { bg: V.textDim + '20', icon: V.textDim, text: V.textDim },
                pending: { bg: V.cardBorder, icon: V.textDim, text: V.textDim },
              };
              const c = colors[state];

              return (
                <div key={stage.key} className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                       style={{ backgroundColor: c.bg }}>
                    {state === 'done' ? <CheckCircle2 size={16} color={c.icon} />
                      : state === 'active' ? <Loader2 size={16} color={c.icon} className="animate-spin" />
                      : state === 'error' ? <XCircle size={16} color={c.icon} />
                      : state === 'cancelled' ? <Ban size={16} color={c.icon} />
                      : <Icon size={16} color={c.icon} />}
                  </div>
                  <span className="text-sm font-semibold" style={{ color: c.text }}>
                    {stage.label}
                  </span>
                  {state === 'active' && (
                    <span className="ml-auto text-xs animate-pulse" style={{ color: V.accent }}>
                      em andamento...
                    </span>
                  )}
                  {state === 'done' && (
                    <CheckCircle2 size={14} className="ml-auto" color={V.success} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Status message */}
          <div className="rounded-xl p-4 mb-4" style={{
            backgroundColor: status === 'failed' ? V.error + '10'
              : status === 'completed' ? V.success + '10'
              : V.accent + '08',
            borderLeft: `3px solid ${status === 'failed' ? V.error : status === 'completed' ? V.success : V.accent}`,
          }}>
            <p className="text-sm" style={{
              color: status === 'failed' ? V.error : status === 'completed' ? V.success : V.textMuted
            }}>
              {getStageMessage(status)}
            </p>
            {session?.error_message && status === 'failed' && (
              <p className="text-xs mt-2 font-mono" style={{ color: V.error + 'cc' }}>
                {session.error_message}
              </p>
            )}
          </div>

          {/* Timer */}
          {!isTerminal && (
            <div className="flex items-center justify-center gap-2 mb-4" style={{ color: V.textDim }}>
              <Timer size={14} />
              <span className="text-xs font-mono">{formatElapsed(elapsed)}</span>
            </div>
          )}

          {/* Error display */}
          {error && (
            <div className="rounded-xl p-3 mb-4 flex items-center gap-2"
                 style={{ backgroundColor: V.error + '15', color: V.error }}>
              <AlertTriangle size={16} />
              <span className="text-xs">{error}</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            {status === 'completed' && (
              <Link
                to={`/swim-vision/result/${sessionId}`}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm transition-all hover:scale-[1.02]"
                style={{ backgroundColor: V.success, color: 'white' }}
              >
                <BarChart3 size={16} />
                Ver Resultado
                <ChevronRight size={16} />
              </Link>
            )}

            {status === 'completed' && (
              <span className="flex items-center text-xs animate-pulse" style={{ color: V.textDim }}>
                Redirecionando...
              </span>
            )}

            {status === 'failed' && (
              <Link
                to="/swim-vision"
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm transition-all hover:scale-[1.02]"
                style={{ backgroundColor: V.accent, color: 'white' }}
              >
                <RefreshCw size={16} />
                Nova Análise
              </Link>
            )}

            {status === 'cancelled' && (
              <Link
                to="/swim-vision"
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm"
                style={{ backgroundColor: V.cardBorder, color: V.text }}
              >
                <ArrowLeft size={16} />
                Voltar
              </Link>
            )}

            {canCancel && (
              <button
                onClick={handleCancel}
                disabled={cancelling}
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-bold text-xs transition-all hover:scale-[1.02] border"
                style={{ borderColor: V.error + '40', color: V.error, backgroundColor: 'transparent' }}
              >
                {cancelling ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}
                Cancelar
              </button>
            )}
          </div>
        </div>

        {/* Back link */}
        <div className="text-center mt-6">
          <Link to="/swim-vision/dashboard" className="text-xs font-semibold inline-flex items-center gap-1 hover:underline"
                style={{ color: V.textDim }}>
            <ArrowLeft size={12} />
            Voltar ao Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}
